import { useAuth } from '../context/AuthContext';
import {useState, useCallback, useEffect} from 'react'
import { AccountDto, ChangeNicknameRequest, SocialAuthDto } from '@playball/common/dto/Account'
import { useNavigate } from 'react-router-dom';

interface UserData {
    uid: number;
    user: AccountDto | null;
    socialAuthList: SocialAuthDto[];
}

const MyPage = () => {
    const {fetchApi, logout} = useAuth();
    const navigate = useNavigate();
    const [nickname, setNickname] = useState<string>('');
    const [userData, setUserData] = useState<UserData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // 백엔드에서 대시보드 데이터를 가져오는 함수
    // useCallback으로 감싸 불필요한 함수 재생성을 방지
    const fetchUserData = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await fetchApi('/api/my');
            setUserData(data);
            
            if (data.user) {
                setNickname(data.user.nickname);
            }
            setError(null);
        } catch (err: any) {
            console.error("대시보드 데이터 로딩 실패:", err);
            setError("데이터를 불러오는 데 실패했습니다. 잠시 후 다시 시도해 주세요.");
        } finally {
            setIsLoading(false);
        }
    }, [fetchApi]);

    const onClickLogoutBtn = useCallback(async () => {
        await logout();
        navigate('/home');
    }, [logout, navigate]);

    const withdraw = useCallback(async () => {
        if (window.confirm('회원 탈퇴 하시겠습니까? 탈퇴 시 되돌릴 수 없습니다')) {
            setIsLoading(true);
            try {
                await fetchApi('/api/my/withdraw', {
                    method: 'POST',
                });
                setError(null);
                alert('회원 탈퇴가 완료되었습니다.');
                await logout();
                navigate('/home');
            } catch (err: any) {
                console.error("회원 탈퇴 실패:", err);
                setError("회원 탈퇴에 실패했습니다. 잠시 후 다시 시도해 주세요.");
            } finally {
                setIsLoading(false);
            }
        }
    }, [fetchApi, logout, navigate]);

    const handleNicknameChange = async (newNickname: string) => {
        if (!userData?.user?.nickname_change_count || newNickname === userData?.user?.nickname) {
            return;
        }

        if (window.confirm('닉네임을 변경하시겠습니까?\n변경 시 횟수가 차감되며, 부적절한 닉네임은 강제로 변경될 수 있습니다')) {
            try {
                const reqBody: ChangeNicknameRequest = {
                    newNickname: newNickname,
                };
                await fetchApi('/api/my', {
                    method: 'PUT',
                    body: JSON.stringify(reqBody),
                });
                alert('닉네임이 변경되었습니다.');
                // 리그 목록에 변경이 생겼으므로, 대시보드 데이터를 다시 불러와 화면을 업데이트합니다.
                fetchUserData();
            } catch (err: any) {
                alert(err.message || '닉네임 변경 도중 오류가 발생했습니다.');
            }
        }
    };

    // 컴포넌트가 처음 마운트될 때 데이터 로딩 함수 호출
    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    if (isLoading) {
        return  <div>로딩중입니다 ..</div>
    }

    if (error) {
        return <div>에러 발생 ..</div>
    }

    if (!userData) {
        return <div>유저 데이터 조회 실패</div>
    }

    const socialAuthMap = new Map(userData.socialAuthList.map(auth => [auth.provider, auth]));

    return(
        
        <div>
            <section className="guide-hero-section py-5 fade-in">
                <h1 className="text-center display-5 fw-bolder">
                    <span className="gradient-text">마이페이지</span>
                </h1>
            </section>

            <div className='container py-4'>
            {/* 사용자 정보 */}
                <div className="bg-light p-4 rounded-3 m-1">
                    <h3 className='mb-4'>개인정보</h3>
                    <div className="mb-2">
                        <label className="form-label">닉네임</label>
                        <input type="text" 
                        id="nickname" 
                        name="nickname" 
                        className="form-control" 
                        value={nickname}
                        onChange={(e) => setNickname(e.target.value)}/>
                        <div className="d-flex align-items-center mt-2">
                            <button id="updateNicknameBtn" onClick={()=>handleNicknameChange(nickname)} className="btn btn-primary" disabled={!userData.user?.nickname_change_count || nickname === userData?.user?.nickname}>
                                변경
                            </button>
                            <p className="form-text text-muted ms-2 mb-0">
                                닉네임 변경 기회가 <b>{userData.user?.nickname_change_count}회</b> 남았습니다. 부적절한 닉네임은 강제로 변경될 수 있습니다.
                            </p>
                        </div>
                    </div>
                    <div className="mb-2">
                        <label className="form-label">이메일</label>
                        <p id="email" 
                        className="form-text">{userData.user?.email}</p>
                    </div>
                    <div className="mb-2">
                        <label className="form-label">포인트</label>
                        <p id="points" className="form-text">{userData.user?.reward_points}</p>
                    </div>
                </div>

            {/* 소셜 계정 연동 정보 */}
                <div className="bg-light p-4 rounded-3 m-1">
                    <h3 className="mb-4">소셜 계정 연동</h3>
                        <ul className="list-group list-group-flush">
                            {['naver', 'kakao', 'apple'].map(provider => {
                                const auth = socialAuthMap.get(provider);
                                return (
                                    <li className="list-group-item d-flex justify-content-between align-items-center" key={provider}>
                                        <span className="text-capitalize">{provider}</span>
                                        {auth ? (
                                            <span className="badge bg-success rounded-pill">연동 완료</span>
                                        ) : (
                                            <></>
                                        )}
                                    </li>
                                );
                                })}
                        </ul>
                </div>
                <div className="bg-light p-4 rounded-3 m-1">
                    <button className="btn btn-secondary" onClick={onClickLogoutBtn}>로그아웃</button>
                    <button className="btn btn-sm btn-link" onClick={withdraw}>회원 탈퇴</button>
                </div>
            </div>
        </div>
    );
}
export default MyPage